import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AllRunsMap from '../components/AllRunsMap.jsx';
import MapFilter from '../components/MapFilter.jsx';
import Footer from '../components/Footer.jsx';
import Loading from '../components/Loading.jsx';
import {
  buildAuthorizeUrl,
  fetchUserActivities,
  getStoredRefToken,
  storeRefToken,
} from '../lib/strava.js';

/**
 * "See YOUR Data!" — connects the visitor's own Strava account and maps their
 * runs. Replaces page-stravalogin.php / page-unique-map.php. All token exchange
 * happens server-side in /api/user-activities so no client secret is exposed.
 */
export default function YourMapPage() {
  const [status, setStatus] = useState('init'); // init | loading | ready | error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [timeFrame, setTimeFrame] = useState('');

  const redirectUri = useMemo(() => `${window.location.origin}/your-map`, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const refToken = params.get('refToken') || getStoredRefToken();

    async function load(args) {
      setStatus('loading');
      try {
        const result = await fetchUserActivities(args);
        if (result.refreshToken) storeRefToken(result.refreshToken);
        const withRoutes = (result.activities || []).filter(
          (a) => a.map && a.map.summary_polyline,
        );
        setData(withRoutes);
        setStatus('ready');
        // Clean the OAuth code out of the URL.
        window.history.replaceState({}, '', '/your-map');
      } catch (e) {
        setError(e.message || 'Something went wrong');
        setStatus('error');
      }
    }

    if (code) {
      load({ code });
    } else if (refToken) {
      load({ refToken });
    } else {
      // No credentials yet — send the visitor to Strava to authorize.
      window.location.replace(buildAuthorizeUrl(redirectUri));
    }
  }, [redirectUri]);

  if (status === 'error') {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '1rem',
          textAlign: 'center',
          padding: '1rem',
        }}
      >
        <p>Sorry — we couldn&apos;t load your Strava data.</p>
        <p style={{ color: 'var(--grey)' }}>{error}</p>
        <a href={buildAuthorizeUrl(redirectUri)}>
          <u>Try connecting to Strava again</u>
        </a>
        <Link to="/">
          <u>Back to Logan&apos;s data</u>
        </Link>
      </div>
    );
  }

  if (status !== 'ready' || !data) {
    return <Loading label="Connecting to Strava…" />;
  }

  return (
    <>
      <MapFilter onFilter={setTimeFrame} />
      <AllRunsMap data={data} timeFrame={timeFrame} style={{ height: '100vh', width: '100vw' }} />
      <Footer />
    </>
  );
}
